import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

// 1. Creamos el contenedor (Contexto) que guardará los datos de sesión.
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Estado para guardar la info del usuario (ej: {email: "admin@test.com", role: "admin"})
  const [user, setUser] = useState(null); 
  
  // Estado para saber si la aplicación ya terminó de verificar si había una sesión previa.
  // Es vital para que no nos mande al login antes de tiempo mientras carga.
  const [ready, setReady] = useState(false);

  // 2. Función de "arranque" (bootstrap). 
  // Revisa si ya teníamos un token guardado en el navegador al abrir la página.
  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("terralot_token");

    // Si no hay token en el almacenamiento local, decimos que estamos "listos" (ready) 
    // pero el usuario se queda en null.
    if (!token) {
      setReady(true);
      return;
    }

    try {
      // Si hay token, le preguntamos al backend (/auth/me) si ese token sigue siendo válido.
      const { data } = await api.get("/auth/me");
      // Si el servidor responde ok, guardamos los datos del usuario.
      setUser(data);
    } catch {
      // Si el token expiró o es falso, lo borramos y limpiamos el estado.
      localStorage.removeItem("terralot_token");
      setUser(null);
    } finally {
      // Pase lo que pase, marcamos que la verificación terminó.
      setReady(true);
    }
  }, []);

  // 3. Este efecto hace que la función bootstrap se ejecute UNA sola vez 
  // en cuanto se carga la aplicación por primera vez.
  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // 4. Función para iniciar sesión.
  const login = async (email, password) => {
    // Enviamos correo y contraseña al backend.
    const { data } = await api.post("/auth/login", { email, password });
    
    // Si es correcto, guardamos el token generado por el servidor en localStorage.
    localStorage.setItem("terralot_token", data.token);
    
    // Actualizamos el estado global del usuario.
    setUser({ email: data.email, role: data.role });
    
    return data;
  };

  // 5. Función para cerrar sesión.
  const logout = () => {
    // Borramos el token para que no se reconozca en la próxima recarga.
    localStorage.removeItem("terralot_token");
    // Limpiamos el usuario.
    setUser(null);
  };

  // 6. Retornamos el Proveedor con los datos y funciones que el resto de la app usará.
  return (
    <AuthContext.Provider value={{ user, ready, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// 7. Hook personalizado para que usar la autenticación sea súper fácil.
// En cualquier componente solo escribes: const { user } = useAuth();
export const useAuth = () => useContext(AuthContext);